import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import { getSuggestionsList } from '../../../services/suggestions';

const SuggestionsContext = createContext();

const isBodyEmpty = (body) => {
  if (body === null || body === undefined) return true;
  if (Array.isArray(body)) return body.length === 0;
  if (typeof body === 'object') return Object.keys(body).length === 0;
  return false;
};

// Helper function to map API response (camelCase) to internal format (snake_case)
const mapSuggestionFromAPI = (suggestion) => {
  if (!suggestion) return null;
  
  return {
    suggestion_uuid: suggestion.suggestionUuid || suggestion.suggestion_uuid,
    fact_uuid: suggestion.factUuid || suggestion.fact_uuid,
    fact_title: suggestion.factTitle || suggestion.fact_title,
    current_value: suggestion.currentValue !== undefined ? suggestion.currentValue : suggestion.current_value,
    new_value: suggestion.newValue !== undefined ? suggestion.newValue : suggestion.new_value,
    comment: suggestion.comment || '',
    status: suggestion.status || 'OPEN',
    risk_level: suggestion.riskLevel !== undefined ? suggestion.riskLevel : suggestion.risk_level,
    created_by: suggestion.createdBy || suggestion.created_by,
    created_by_role: suggestion.createdByRole || suggestion.created_by_role,
    created_by_photo: suggestion.createdByPhoto !== undefined ? suggestion.createdByPhoto : suggestion.created_by_photo,
    created_at: suggestion.createdAt || suggestion.created_at,
    knowledgebase_uuid: suggestion.knowledgebaseUuid || suggestion.knowledgebase_uuid,
  };
};

const initialState = {
  baseSuggestions: [],
  baseMeta: null,
  baseKnowledgebaseUuid: null,
  suggestions: [],
  suggestionsMeta: null,
  loading: false,
  error: null,
  isEmpty: false,
  alert: null,
};

export const SuggestionsProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);

  const fetchSuggestions = useCallback(
    async (params = {}, options = {}) => {
      const { persistAsBase = false } = options;
      const requestId = ++fetchRequestIdRef.current;

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        isEmpty: false,
      }));

      try {
        const response = await getSuggestionsList(params);

        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        const body = Array.isArray(response.body) ? response.body : [];
        // Map API response (camelCase) to internal format (snake_case)
        const mappedBody = body.map(mapSuggestionFromAPI).filter(Boolean);
        const isEmpty = isBodyEmpty(mappedBody);
        const knowledgebaseUuid = params?.knowledgebase_uuid ?? null;

        if (isEmpty) {
          setState((prev) => ({
            ...prev,
            baseSuggestions: persistAsBase ? [] : prev.baseSuggestions,
            baseMeta: persistAsBase ? null : prev.baseMeta,
            baseKnowledgebaseUuid: persistAsBase ? null : prev.baseKnowledgebaseUuid,
            suggestions: [],
            suggestionsMeta: response.meta || null,
            loading: false,
            error: null,
            isEmpty: true,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            baseSuggestions: persistAsBase ? mappedBody : prev.baseSuggestions,
            baseMeta: persistAsBase ? (response.meta || null) : prev.baseMeta,
            baseKnowledgebaseUuid: persistAsBase ? knowledgebaseUuid : prev.baseKnowledgebaseUuid,
            suggestions: mappedBody,
            suggestionsMeta: response.meta || null,
            loading: false,
            error: null,
            isEmpty: false,
          }));
        }
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        console.error('Error fetching suggestions:', error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error,
          suggestions: [],
          suggestionsMeta: null,
          isEmpty: false,
          ...(persistAsBase
            ? {
                baseSuggestions: [],
                baseMeta: null,
                baseKnowledgebaseUuid: null,
              }
            : {}),
        }));
      }
    },
    []
  );

  const resetSuggestionsToBase = useCallback(() => {
    fetchRequestIdRef.current = fetchRequestIdRef.current + 1;

    setState((prev) => ({
      ...prev,
      suggestions: prev.baseSuggestions,
      suggestionsMeta: prev.baseMeta,
      loading: false,
    }));
  }, []);

  const resetSuggestions = useCallback(() => {
    setState(initialState);
    fetchRequestIdRef.current = 0;
  }, []);

  const removeSuggestion = useCallback((suggestion_uuid) => {
    setState((prev) => ({
      ...prev,
      suggestions: prev.suggestions.filter(
        (suggestion) => suggestion.suggestion_uuid !== suggestion_uuid
      ),
      baseSuggestions: prev.baseSuggestions.filter(
        (suggestion) => suggestion.suggestion_uuid !== suggestion_uuid
      ),
      // Actualizar meta si es necesario
      suggestionsMeta: prev.suggestionsMeta
        ? {
            ...prev.suggestionsMeta,
            totalItems: Math.max(0, (prev.suggestionsMeta.totalItems || 0) - 1),
          }
        : null,
      baseMeta: prev.baseMeta
        ? {
            ...prev.baseMeta,
            totalItems: Math.max(0, (prev.baseMeta.totalItems || 0) - 1),
          }
        : null,
    }));
  }, []);

  const setAlert = useCallback((alertData) => {
    setState((prev) => ({
      ...prev,
      alert: alertData,
    }));
  }, []);

  const clearAlert = useCallback(() => {
    setState((prev) => ({
      ...prev,
      alert: null,
    }));
  }, []);

  const value = {
    baseSuggestions: state.baseSuggestions,
    baseMeta: state.baseMeta,
    baseKnowledgebaseUuid: state.baseKnowledgebaseUuid,
    suggestions: state.suggestions,
    suggestionsMeta: state.suggestionsMeta,
    loading: state.loading,
    error: state.error,
    isEmpty: state.isEmpty,
    alert: state.alert,
    fetchSuggestions,
    resetSuggestionsToBase,
    resetSuggestions,
    removeSuggestion,
    setAlert,
    clearAlert,
  };

  return (
    <SuggestionsContext.Provider value={value}>
      {children}
    </SuggestionsContext.Provider>
  );
};

export const useSuggestionsContext = () => {
  const context = useContext(SuggestionsContext);
  if (!context) {
    throw new Error('useSuggestionsContext must be used within SuggestionsProvider');
  }
  return context;
};

